import { useCallback, useMemo, useState } from "react";

export type IntentPhase =
  "queued" | "sending" | "awaiting_snapshot" | "outcome_unknown";

interface IntentRecord {
  id: string;
  key: string;
  phase: IntentPhase;
  retry: (() => void) | null;
}

export interface IntentToken {
  id: string;
  markSending: () => void;
  markAwaitingSnapshot: () => void;
  markOutcomeUnknown: (retry: () => void) => void;
  finish: () => void;
}

export interface IntentStatusView {
  pendingCount: number;
  isPending: (key: string) => boolean;
  countFor: (prefix: string) => number;
  phaseFor: (key: string) => IntentPhase | null;
  unknownCount: number;
  unknownCountFor: (prefix: string) => number;
  retryUnknown: () => void;
}

export interface IntentStatusController {
  view: IntentStatusView;
  begin: (key: string) => IntentToken;
  track: <T>(key: string, operation: () => Promise<T>) => Promise<T>;
}

export const EMPTY_INTENT_STATUS: IntentStatusView = {
  pendingCount: 0,
  isPending: () => false,
  countFor: () => 0,
  phaseFor: () => null,
  unknownCount: 0,
  unknownCountFor: () => 0,
  retryUnknown: () => {},
};

export function useIntentStatus(): IntentStatusController {
  const [records, setRecords] = useState<IntentRecord[]>([]);

  const begin = useCallback((key: string): IntentToken => {
    const id = crypto.randomUUID();
    let finished = false;
    setRecords((current) => [
      ...current,
      { id, key, phase: "queued", retry: null },
    ]);
    const setPhase = (
      phase: IntentPhase,
      retry: (() => void) | null = null,
    ) => {
      if (finished) return;
      setRecords((current) =>
        current.map((record) =>
          record.id === id ? { ...record, phase, retry } : record,
        ),
      );
    };
    return {
      id,
      markSending: () => setPhase("sending"),
      markAwaitingSnapshot: () => setPhase("awaiting_snapshot"),
      markOutcomeUnknown: (retry) => setPhase("outcome_unknown", retry),
      finish: () => {
        if (finished) return;
        finished = true;
        setRecords((current) => current.filter((record) => record.id !== id));
      },
    };
  }, []);

  const track = useCallback(
    async <T>(key: string, operation: () => Promise<T>): Promise<T> => {
      const token = begin(key);
      token.markSending();
      try {
        return await operation();
      } finally {
        token.finish();
      }
    },
    [begin],
  );

  const view = useMemo<IntentStatusView>(() => {
    const isPending = (key: string) =>
      records.some((record) => record.key === key);
    const countFor = (prefix: string) =>
      records.filter((record) => record.key.startsWith(prefix)).length;
    const phaseFor = (key: string): IntentPhase | null => {
      const matching = records.filter((record) => record.key === key);
      if (matching.some((record) => record.phase === "outcome_unknown")) {
        return "outcome_unknown";
      }
      if (matching.some((record) => record.phase === "sending")) {
        return "sending";
      }
      if (matching.some((record) => record.phase === "queued")) {
        return "queued";
      }
      return matching.length ? "awaiting_snapshot" : null;
    };
    const unknown = records.filter(
      (record) => record.phase === "outcome_unknown",
    );
    return {
      pendingCount: records.length,
      isPending,
      countFor,
      phaseFor,
      unknownCount: unknown.length,
      unknownCountFor: (prefix: string) =>
        unknown.filter((record) => record.key.startsWith(prefix)).length,
      retryUnknown: () => unknown.forEach((record) => record.retry?.()),
    };
  }, [records]);

  return { view, begin, track };
}
